if (!customElements.get("media-gallery")) {
  customElements.define(
    "media-gallery",
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        // 这一句很重要，Dawn 用它来判断是否大于 750px (PC 端)
        this.mql = window.matchMedia("(min-width: 750px)");
        
        // 1. 解析我们在 Liquid 中设置的隐藏主图重定向映射表
        this.redirectMap = {};
        const redirectMapStr = this.getAttribute("data-redirect-map");
        if (redirectMapStr) {
          redirectMapStr.split(",").forEach((pair) => {
            if (pair) {
              const parts = pair.split(":");
              if (parts.length === 2) {
                this.redirectMap[parts[0]] = parts[1];
              }
            }
          });
        }

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener(
          "slideChanged",
          debounce(this.onSlideChanged.bind(this), 500),
        );
        this.elements.thumbnails
          .querySelectorAll("[data-target]")
          .forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector("button")
              .addEventListener(
                "click",
                this.setActiveMedia.bind(
                  this,
                  mediaToSwitch.dataset.target,
                  false,
                ),
              );
          });
        if (
          this.dataset.desktopLayout.includes("thumbnail") &&
          this.mql.matches
        )
          this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`,
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        // 彻底禁止 Dawn 的物理 DOM 重排！
        prepend = false; 

        // ==========================================================
        // [新增核心] 设备合规性检测函数
        // 判断一张图片在当前设备下是否真正可见
        // ==========================================================
        const isDesktop = this.mql.matches;
        const isValidDeviceMedia = (el) => {
          if (!el) return false;
          if (el.classList.contains('hidden-by-variant')) return false; // 被变体隐藏
          
          if (isDesktop) {
            // PC 端：排除带有移动端隐藏类的图
            if (el.classList.contains('medium-hide') || el.classList.contains('large-up-hide')) return false;
          } else {
            // 移动端：排除带有 PC 端隐藏类的图
            if (el.classList.contains('small-hide')) return false;
          }
          return true;
        };

        let targetMediaId = mediaId;

        // 尝试获取目标图片
        let activeMedia = this.elements.viewer.querySelector(`[data-media-id="${targetMediaId}"]`) ||
                          this.elements.viewer.querySelector(`[data-media-id*="${targetMediaId}"]`);

        // 如果找不到，或者找到的这图在【当前设备不可见】
        if (!activeMedia || !isValidDeviceMedia(activeMedia)) {
            // 尝试通过映射表转换
            for (const [featId, subId] of Object.entries(this.redirectMap)) {
              if (targetMediaId && targetMediaId.includes(featId)) {
                targetMediaId = targetMediaId.replace(featId, subId);
                break;
              }
            }
            activeMedia = this.elements.viewer.querySelector(`[data-media-id="${targetMediaId}"]`) ||
                          this.elements.viewer.querySelector(`[data-media-id*="${targetMediaId}"]`);
        }

        // 终极强制覆盖：如果依然找不到，或者找到的这图处于隐藏状态/设备不合规
        if (!activeMedia || !isValidDeviceMedia(activeMedia)) {
          // 霸道逻辑：抓取当前变体下，符合【当前设备】的第一张可见图！
          const allVariantItems = Array.from(this.querySelectorAll('.product__media-item:not(.hidden-by-variant)'));
          activeMedia = allVariantItems.find(isValidDeviceMedia);
          
          // 极端的防错保底（万一代码没写好全被过滤了，随便抓第一张防白屏）
          if (!activeMedia && allVariantItems.length > 0) {
            activeMedia = allVariantItems[0];
          }
        }

        // 保护机制
        if (!activeMedia) return;

        // 更新正确的 mediaId 供下方原生逻辑使用
        mediaId = activeMedia.getAttribute('data-media-id') || targetMediaId;

        // 移除其他图的高亮，给目标图加上高亮
        this.elements.viewer
          .querySelectorAll("[data-media-id]")
          .forEach((element) => {
            element.classList.remove("is-active");
          });
        activeMedia.classList.add("is-active");

        // 处理视频的暂停逻辑 (如果在视频上切变体，会自动暂停它)
        this.playActiveMedia(activeMedia);

        // 利用纯净的 scrollTo 进行平滑滚动，不破坏 DOM 结构
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({
              left: activeMedia.offsetLeft,
              behavior: "smooth"
            });
          }
          // 如果用户往下滚动了网页，点击变体时稍微把图片滚回视野
          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: "smooth" });
        }, 10);

        // 同步缩略图状态
        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${mediaId}"]`,
        );
        this.setActiveThumbnail(activeThumbnail);
        
        if (activeThumbnail) {
          this.announceLiveRegion(
            activeMedia,
            activeThumbnail.dataset.mediaPosition,
          );
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll("button")
          .forEach((element) => element.removeAttribute("aria-current"));
        thumbnail.querySelector("button").setAttribute("aria-current", "true");
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({
          left: thumbnail.offsetLeft,
          behavior: "smooth"
        });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector(
          ".product__modal-opener--image img",
        );
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute("aria-hidden", false);
          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings.imageAvailable.replace(
              "[index]",
              position,
            );
          setTimeout(() => {
            this.elements.liveRegion.setAttribute("aria-hidden", true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        // 原生自带停止当前正在播放视频的功能
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector(".deferred-media");
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader =
          this.stickyHeader || document.querySelector("sticky-header");
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event("preventHeaderReveal"));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute("role", "presentation");
        this.elements.viewer.sliderItems.forEach((slide) =>
          slide.setAttribute("role", "presentation"),
        );
      }
    },
  );
}